import { ReactNode, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useGetAllDocuments, useUploadDocument } from "src/api/client-api/documentation";
import { useDeleteMediaContent } from "src/api/client-api/cms/useDeleteMediaContent";
import { downloadFile } from "src/utils/download";
import { FilePreviewModal } from "../components/FilePreviewModal";
import { UploadMediaDialog } from "../components/UploadMediaDialog";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { MediaLibraryContext, MediaItem, MediaActionHandlers, MediaLibraryContextValues } from "./context";

const PAGE_SIZE = 20;

type MediaLibraryContextProviderProps = {
    children: ReactNode;
    onSelectItem?: (item: MediaItem) => void;
    allowedMimeTypes?: string[];
};

export const MediaLibraryContextProvider = (props: MediaLibraryContextProviderProps) => {
    const { children, onSelectItem, allowedMimeTypes } = props;

    const [view, setView] = useState<"list" | "grid">("list");

    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const [mediaToDelete, setMediaToDelete] = useState<string[]>([]);
    const [selectedItems, setSelectedItems] = useState<string[]>([]);

    const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "createdAt", desc: true }]);
    const [columnFilters, setColumnFilters] = useState<{ id: string; value: any }[]>([]);
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    const handleSetView = (newView: "list" | "grid") => {
        setSearch("");
        setColumnFilters([]);
        setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
        setView(newView);
    };

    const mimeTypeFilter = columnFilters.find((filter) => filter.id === "mimeType")?.value || "";
    const nameFilter = columnFilters.find((filter) => filter.id === "name")?.value || "";

    const searchQuery = view === "grid" ? delayedSearch : nameFilter || delayedSearch;
    const mimeTypeQuery = view === "list" ? mimeTypeFilter : "";

    const { data, isLoading, isError, error, refetch } = useGetAllDocuments({
        page: pagination,
        sorting,
        search: searchQuery,
        mimeType: mimeTypeQuery,
    });

    const isForbidden = isAxiosError(error) && error.response?.status === 403;

    const { query: uploadMutation, uploading: isUploading } = useUploadDocument();
    const { query: deleteMutation, deleting: isDeleting } = useDeleteMediaContent();

    const mediaActions = useMemo<MediaActionHandlers>(
        () => ({
            handleUpload: (files: File[], names: string[]) => {
                const totalFiles = files.length;
                let completedUploads = 0;

                const processNextFile = (index: number) => {
                    if (index >= totalFiles) {
                        setIsUploadDialogOpen(false);
                        refetch();
                        return;
                    }

                    uploadMutation.mutate(
                        { file: files[index], name: names[index], mimeType: files[index].type },
                        {
                            onSuccess: () => {
                                completedUploads++;
                                if (completedUploads === totalFiles) {
                                    setIsUploadDialogOpen(false);
                                    refetch();
                                } else {
                                    processNextFile(index + 1);
                                }
                            },
                            onError: (error) => {
                                console.error("Error uploading file:", error);
                                processNextFile(index + 1);
                            },
                        },
                    );
                };

                processNextFile(0);
            },
            handleToggleSelect: (id: string) => {
                setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
            },
            handleSetSelectedItems: setSelectedItems,
            handleSelectAll: () => {
                if (selectedItems.length === data?.list?.length) {
                    setSelectedItems([]);
                } else {
                    setSelectedItems(data?.list?.map((item) => item.id) || []);
                }
            },
            handleDelete: (id: string) => {
                setMediaToDelete([id]);
                setDeleteConfirmDialogOpen(true);
            },
            confirmDeleteSelected: () => {
                setMediaToDelete(selectedItems);
                setDeleteConfirmDialogOpen(true);
            },
            handleDeleteConfirm: () => {
                if (mediaToDelete.length === 0) return;

                const deletePromises = mediaToDelete.map((id) => deleteMutation.mutateAsync({ id }));

                Promise.all(deletePromises)
                    .then(() => {
                        setDeleteConfirmDialogOpen(false);
                        setMediaToDelete([]);
                        setSelectedItems([]);
                        refetch();
                    })
                    .catch((error) => {
                        console.error("Error deleting media:", error);
                    });
            },
            handleDeleteCancel: () => {
                setDeleteConfirmDialogOpen(false);
                setMediaToDelete([]);
            },
            handleDownload: async (item: MediaItem) => {
                if (!item.url || !item.name) return;
                try {
                    await downloadFile({ url: item.url, filename: item.name, mimeType: item.mimeType });
                } catch (error) {
                    console.error("Error downloading file:", error);
                }
            },
            handlePreview: (item: MediaItem) => {
                if (item && item.mimeType) {
                    const isImage = item.mimeType.startsWith("image/");
                    const isVideo = item.mimeType.startsWith("video/");
                    const isPdf = item.mimeType === "application/pdf";

                    if (isImage || isVideo || isPdf) {
                        setPreviewItem(item);
                        setPreviewOpen(true);
                    } else {
                        mediaActions.handleDownload(item);
                    }
                }
            },
            handleCopyUrl: (url: string) => {
                if (!url) return;
                navigator.clipboard.writeText(url).catch((err) => console.error("Failed to copy URL", err));
            },
        }),
        [data?.list, mediaToDelete, selectedItems, deleteMutation, uploadMutation, refetch],
    );

    const contextValue: MediaLibraryContextValues = {
        onSelectItem,
        media: data?.list,
        totalItems: data?.totalItems || 0,
        isLoading,
        isError,
        isForbidden,
        refetch,
        search,
        setSearch,
        columnFilters,
        setColumnFilters,
        sorting,
        setSorting,
        pagination,
        setPagination,
        view,
        setView: handleSetView,
        selectedItems,
        mediaActions,
        isUploadDialogOpen,
        setIsUploadDialogOpen,
        deleteConfirmDialogOpen,
        isDeleting,
        isUploading,
        mediaToDelete,
        allowedMimeTypes,
    };

    return (
        <MediaLibraryContext.Provider value={contextValue}>
            {children}
            <UploadMediaDialog
                open={isUploadDialogOpen}
                onClose={() => setIsUploadDialogOpen(false)}
                onUpload={mediaActions.handleUpload}
                isUploading={isUploading}
                allowedMimeTypes={allowedMimeTypes}
            />
            <DeleteConfirmDialog
                open={deleteConfirmDialogOpen}
                onClose={mediaActions.handleDeleteCancel}
                onConfirmDelete={mediaActions.handleDeleteConfirm}
                deleteCount={mediaToDelete.length}
                isDeleting={isDeleting}
                fileName={(() => {
                    if (mediaToDelete.length !== 1) return undefined;
                    const fileToDelete = data?.list?.find((item: MediaItem) => item.id === mediaToDelete[0]);
                    return fileToDelete?.name;
                })()}
            />
            {previewItem && (
                <FilePreviewModal
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    onDownload={mediaActions.handleDownload}
                    mediaItem={{
                        id: previewItem.id,
                        name: previewItem.name || "",
                        url: previewItem.url || "",
                        mimeType: previewItem.mimeType || "",
                        size: previewItem.size || 0,
                        createdAt: previewItem.createdAt,
                        updatedAt: previewItem.updatedAt || "",
                        canView: previewItem.canView ?? true,
                        canDelete: previewItem.canDelete ?? false,
                    }}
                />
            )}
        </MediaLibraryContext.Provider>
    );
};
